/* eslint-disable */
import React, { useState, useEffect, useMemo } from "react";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { doc, setDoc, deleteField, serverTimestamp } from "firebase/firestore";
import { db } from "../../../firebase";
import { Spin } from "antd";
import {
  ChevronDownIcon,
  ChevronUpIcon,
  ClipboardDocumentIcon,
  CheckIcon,
} from "@heroicons/react/24/solid";
import { ALGERIAN_SHIPPING_COMPANIES } from "../../../constants/algerian_shipping_companies";
import {
  PRINT_AGENT_EVENTS,
  WEBHOOK_BASE_URL,
} from "../../../constants/printAgentEvents";
import Swal from "sweetalert2";

export default function ActivationPrintAgent({ close }) {
  const { t, i18n } = useTranslation();
  const store = useSelector((state) => state.StoreReducer);
  const isRTL = i18n.language === "ar";

  const [loading, setLoading] = useState(false);
  const [expandedCompany, setExpandedCompany] = useState(null);
  const [unlinkedOpen, setUnlinkedOpen] = useState(false);

  // local state for selected events per company (before saving)
  const [selectedEvents, setSelectedEvents] = useState({});
  // saved configuration from Firestore
  const [savedConfig, setSavedConfig] = useState({});
  const [guepexSecret, setGuepexSecret] = useState("");

  const allCompanies = useMemo(() => {
    return ALGERIAN_SHIPPING_COMPANIES.filter(
      (c) => c.key !== "other" && c.supportsWebhook === true,
    ).map((c) => ({
      ...c,
      name: t(`services.foorCall.companies.${c.nameKey}`, {
        defaultValue: c.nameKey,
      }),
      isLinked: c.check(store),
      supportsWebhook: !!PRINT_AGENT_EVENTS[c.key],
    }));
  }, [store, t]);

  const linkedCompanies = allCompanies.filter((c) => c.isLinked);
  const unlinkedCompanies = allCompanies.filter((c) => !c.isLinked);

  // load existing configuration
  useEffect(() => {
    if (!store?.user) return;

    const config = store.printAgentConfig || {};
    setSavedConfig(config);

    // initialize selectedEvents from saved config
    const initial = {};
    Object.keys(config).forEach((companyKey) => {
      initial[companyKey] = config[companyKey]?.events || [];
    });
    setSelectedEvents(initial);
    setGuepexSecret(store.SecretKeyGuepex || "");
  }, [store?.user, store?.printAgentConfig, store?.SecretKeyGuepex]);

  const getWebhookUrl = (companyKey) => {
    // ecotrack
    if (companyKey === "ecotrack" && store?.Ecotrack) {
      return `${WEBHOOK_BASE_URL}/webhooks/ecotrack/${store.Ecotrack}`;
    }

    // zr express
    if (companyKey === "zrexpress" && store?.zrExpressXTenant_V2) {
      return `${WEBHOOK_BASE_URL}/webhooks/zr-express/${store.zrExpressXTenant_V2}`;
    }

    // ecom delivery v2
    if (companyKey === "ecomdelivery" && store?.user) {
      return `${WEBHOOK_BASE_URL}/webhooks/ecom/${store.user}`;
    }

    // guepex
    if (companyKey === "guepex" && store?.UserguidGuepex) {
      return `${WEBHOOK_BASE_URL}/webhooks/guepex/${store.UserguidGuepex}`;
    }

    return null;
  };

  const isConfigured = (companyKey) => {
    return !!savedConfig[companyKey]?.configured;
  };

  const selectEvent = (companyKey, eventKey, eventName) => {
    if (isConfigured(companyKey)) return;

    setSelectedEvents((prev) => ({
      ...prev,
      [companyKey]: [{ key: eventKey, event: eventName }],
    }));
  };

  const handleSave = async (companyKey) => {
    const events = selectedEvents[companyKey] || [];
    if (events.length === 0) {
      Swal.fire({
        icon: "warning",
        title: t("services.printAgent.selectAtLeastOne"),
        confirmButtonColor: "#9b59bf",
      });
      return;
    }

    // guepex needs secret key
    if (companyKey === "guepex" && !guepexSecret.trim()) {
      Swal.fire({
        icon: "warning",
        title: t("services.printAgent.guepexSecretRequired"),
        confirmButtonColor: "#9b59bf",
      });
      return;
    }

    setLoading(true);
    try {
      // zr express: call backend to register webhook
      if (companyKey === "zrexpress") {
        const res = await fetch(
          `${WEBHOOK_BASE_URL}/webhooks/zr-express/registration/activate`,
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ storeId: store.user }),
          },
        );

        const data = await res.json().catch(() => ({}));

        if (!res.ok) {
          throw new Error(
            data.message ||
              data.detail ||
              (Array.isArray(data.message) ? data.message.join(", ") : null) ||
              `Activation failed (${res.status})`,
          );
        }
      }

      const companyConfig = {
        configured: true,
        events,
        configuredAt: serverTimestamp(),
      };

      const newConfig = {
        ...savedConfig,
        [companyKey]: companyConfig,
      };

      const updatePayload = {
        printAgentConfig: newConfig,
        printAgentActive: true,
      };
      if (companyKey === "guepex") {
        updatePayload.SecretKeyGuepex = guepexSecret.trim();
      }

      await setDoc(doc(db, "store", store.id), updatePayload, { merge: true });

      setSavedConfig(newConfig);

      Swal.fire({
        icon: "success",
        title: t("services.printAgent.savedSuccessfully"),
        confirmButtonColor: "#9b59bf",
      });
    } catch (err) {
      Swal.fire({
        icon: "error",
        title: t("services.printAgent.errorSaving"),
        text: err?.message || String(err),
        confirmButtonColor: "#9b59bf",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleUnconfig = async (companyKey) => {
    const result = await Swal.fire({
      icon: "warning",
      title: t("services.printAgent.confirmUnconfig"),
      showCancelButton: true,
      confirmButtonText: t("services.printAgent.yesUnconfig"),
      cancelButtonText: t("services.printAgent.cancel"),
      confirmButtonColor: "#e87778",
      cancelButtonColor: "#9b59bf",
    });

    if (!result.isConfirmed) return;

    setLoading(true);
    try {
      const newConfig = { ...savedConfig };
      delete newConfig[companyKey];

      const remainingCompanies = Object.keys(newConfig);
      const isLastOne = remainingCompanies.length === 0;

      const updateData = {
        printAgentConfig: {
          [companyKey]: deleteField(),
        },
      };

      if (companyKey === "guepex") {
        updateData.SecretKeyGuepex = deleteField();
      }

      if (isLastOne) {
        updateData.printAgentActive = false;
      }

      await setDoc(doc(db, "store", store.id), updateData, { merge: true });

      if (companyKey === "guepex") {
        setGuepexSecret("");
      }

      setSavedConfig(newConfig);

      setSelectedEvents((prev) => {
        const next = { ...prev };
        delete next[companyKey];
        return next;
      });

      Swal.fire({
        icon: "success",
        title: t("services.printAgent.savedSuccessfully"),
        confirmButtonColor: "#9b59bf",
        timer: 1500,
        showConfirmButton: false,
      });
    } catch (err) {
      Swal.fire({
        icon: "error",
        title: t("services.printAgent.errorSaving"),
        confirmButtonColor: "#9b59bf",
      });
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      Swal.fire({
        icon: "success",
        title: t("services.printAgent.copied"),
        timer: 1500,
        showConfirmButton: false,
      });
    } catch {
      const el = document.createElement("textarea");
      el.value = text;
      document.body.appendChild(el);
      el.select();
      document.execCommand("copy");
      document.body.removeChild(el);
    }
  };

  const renderCompanySection = (company) => {
    const isOpen = expandedCompany === company.key;
    const configured = isConfigured(company.key);
    const events = PRINT_AGENT_EVENTS[company.key] || [];
    const currentSelected = selectedEvents[company.key] || [];
    const webhookUrl = getWebhookUrl(company.key);

    return (
      <div
        key={company.key}
        className="border border-[#fbf4ff] dark:!border-[#3c3a3d] rounded-xl overflow-hidden bg-white dark:!bg-[#1f1f1f]"
      >
        <button
          type="button"
          onClick={() => setExpandedCompany(isOpen ? null : company.key)}
          className="w-full flex items-center gap-3 p-4 hover:bg-[#fcf6ff]/50 dark:hover:bg-[#2a2a2a]/50 transition-colors"
        >
          <div className="w-10 h-10 rounded-lg bg-[#fcf6ff] dark:!bg-[#181819] flex items-center justify-center overflow-hidden shrink-0">
            {company.logoUrl ? (
              <img
                src={company.logoUrl}
                alt={company.name}
                className="w-full h-full object-contain"
              />
            ) : (
              <span className="text-xs font-bold text-[#9b59bf]">
                {company.name.slice(0, 2)}
              </span>
            )}
          </div>

          <div className="flex-1 text-start">
            <span className="font-semibold text-gray-800 dark:!text-gray-100">
              {company.name}
            </span>
          </div>

          <div
            className={`w-6 h-6 rounded-full border-2 flex items-center justify-center shrink-0 ${
              configured
                ? "bg-emerald-500 border-emerald-500"
                : "border-gray-300 dark:!border-[#3c3a3d]"
            }`}
          >
            {configured && <CheckIcon className="w-3.5 h-3.5 text-white" />}
          </div>

          {isOpen ? (
            <ChevronUpIcon className="w-5 h-5 text-gray-400" />
          ) : (
            <ChevronDownIcon className="w-5 h-5 text-gray-400" />
          )}
        </button>

        {isOpen && (
          <div className="px-4 pb-5 pt-1 border-t border-[#fbf4ff] dark:!border-[#3c3a3d] space-y-5">
            {/* zr express */}
            {company.key === "zrexpress" && (
              <>
                <div>
                  <p className="text-sm font-medium text-gray-600 dark:!text-gray-300 mb-4 text-center">
                    {t("services.printAgent.selectEvents")}
                  </p>
                  <div className="flex flex-wrap items-center justify-center gap-3">
                    {events.map((ev) => {
                      const checked = currentSelected[0]?.key === ev.key;
                      return (
                        <button
                          key={ev.key}
                          type="button"
                          disabled={configured}
                          onClick={() =>
                            selectEvent(company.key, ev.key, ev.event)
                          }
                          className={`
                            relative flex items-center gap-2 px-5 py-2.5 rounded-full border-2 text-sm font-medium
                            transition-all duration-200
                            ${configured ? "opacity-60 cursor-not-allowed" : "cursor-pointer hover:scale-[1.02]"}
                            ${
                              checked
                                ? "bg-[#9b59bf] border-[#9b59bf] text-white shadow-md shadow-[#9b59bf]/25"
                                : "bg-[#9b59bf]/10 border-[#9b59bf]/40 text-[#9b59bf] dark:text-[#bc7ade] hover:bg-[#9b59bf]/15"
                            }
                          `}
                        >
                          {checked && (
                            <CheckIcon className="w-4 h-4 text-white shrink-0" />
                          )}
                          <span>
                            {t(`services.printAgent.events.${ev.key}`, {
                              defaultValue: ev.fr,
                            })}
                          </span>
                        </button>
                      );
                    })}
                  </div>
                </div>

                <div className="bg-[#fcf6ff] dark:!bg-[#222222] border border-[#fbf4ff] dark:!border-[#3c3a3d] rounded-xl p-4 text-sm text-gray-700 dark:!text-gray-300 leading-relaxed">
                  <p className="font-medium text-[#9e68ba] mb-2">
                    {t("services.printAgent.zrExpressNoteTitle")}
                  </p>
                  <p>{t("services.printAgent.zrExpressNote")}</p>
                </div>

                <div className="flex justify-end gap-3 pt-1">
                  {configured ? (
                    <button
                      type="button"
                      onClick={() => handleUnconfig(company.key)}
                      disabled={loading}
                      className="px-5 py-2.5 rounded-lg bg-red-500 hover:bg-red-600 text-white text-sm font-medium transition-colors disabled:opacity-50"
                    >
                      {t("services.printAgent.unconfig")}
                    </button>
                  ) : (
                    <button
                      type="button"
                      onClick={() => handleSave(company.key)}
                      disabled={loading || currentSelected.length === 0}
                      className="px-5 py-2.5 rounded-lg bg-[#9b59bf] hover:bg-[#814a9f] text-white text-sm font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {loading ? (
                        <Spin size="small" />
                      ) : (
                        t("services.printAgent.activate")
                      )}
                    </button>
                  )}
                </div>
              </>
            )}

            {/* guepex */}
            {company.key === "guepex" && (
              <>
                <div>
                  <p className="text-sm font-medium text-gray-600 dark:!text-gray-300 mb-4 text-center">
                    {t("services.printAgent.selectEvents")}
                  </p>
                  <div className="flex flex-wrap items-center justify-center gap-3">
                    {events.map((ev) => {
                      const checked = currentSelected[0]?.key === ev.key;
                      return (
                        <button
                          key={ev.key}
                          type="button"
                          disabled={configured}
                          onClick={() =>
                            selectEvent(company.key, ev.key, ev.event)
                          }
                          className={`
                            relative flex items-center gap-2 px-5 py-2.5 rounded-full border-2 text-sm font-medium
                            transition-all duration-200
                            ${configured ? "opacity-60 cursor-not-allowed" : "cursor-pointer hover:scale-[1.02]"}
                            ${
                              checked
                                ? "bg-[#9b59bf] border-[#9b59bf] text-white shadow-md shadow-[#9b59bf]/25"
                                : "bg-[#9b59bf]/10 border-[#9b59bf]/40 text-[#9b59bf] dark:text-[#bc7ade] hover:bg-[#9b59bf]/15"
                            }
                          `}
                        >
                          {checked && (
                            <CheckIcon className="w-4 h-4 text-white shrink-0" />
                          )}
                          <span>
                            {t(`services.printAgent.events.${ev.key}`, {
                              defaultValue: ev.fr,
                            })}
                          </span>
                        </button>
                      );
                    })}
                  </div>
                </div>

                {webhookUrl && (
                  <div>
                    <p className="text-sm font-medium text-gray-600 dark:!text-gray-300 mb-2">
                      {t("services.printAgent.webhookUrl")}
                    </p>
                    <div className="flex items-center gap-2">
                      <input
                        type="text"
                        readOnly
                        value={webhookUrl}
                        className="flex-1 bg-[#fcf6ff] dark:!bg-[#141414] border border-[#fbf4ff] dark:!border-[#3c3a3d] rounded-lg px-3 py-2.5 text-sm font-mono text-gray-800 dark:!text-gray-200 truncate"
                        onClick={(e) => e.target.select()}
                      />
                      <button
                        type="button"
                        onClick={() => copyToClipboard(webhookUrl)}
                        className="flex items-center gap-1.5 px-3 py-2.5 rounded-lg bg-[#9b59bf] hover:bg-[#814a9f] text-white text-sm font-medium shrink-0"
                      >
                        <ClipboardDocumentIcon className="w-4 h-4" />
                        {t("services.printAgent.copy")}
                      </button>
                    </div>
                  </div>
                )}

                <div className="bg-[#fcf6ff] dark:!bg-[#222222] border border-[#fbf4ff] dark:!border-[#3c3a3d] rounded-xl p-4 text-sm text-gray-700 dark:!text-gray-300 leading-relaxed">
                  <p className="font-medium text-[#9e68ba] mb-2">
                    {t("services.printAgent.instructionsTitle")}
                  </p>
                  <ol className="list-decimal list-inside space-y-1.5">
                    <li>{t("services.printAgent.guepexStep1")}</li>
                    <li>{t("services.printAgent.guepexStep2")}</li>
                    <li>{t("services.printAgent.guepexStep3")}</li>
                    <li>{t("services.printAgent.guepexStep4")}</li>
                  </ol>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-600 dark:!text-gray-300 mb-2">
                    {t("services.printAgent.guepexSecretLabel")}
                  </label>
                  <input
                    type="text"
                    value={guepexSecret}
                    onChange={(e) => {
                      if (!configured) setGuepexSecret(e.target.value);
                    }}
                    readOnly={configured}
                    disabled={configured}
                    placeholder={t(
                      "services.printAgent.guepexSecretPlaceholder",
                    )}
                    className={`w-full bg-[#fcf6ff] dark:!bg-[#141414] border border-[#fbf4ff] dark:!border-[#3c3a3d] rounded-lg px-3 py-2.5 text-sm text-gray-800 dark:!text-gray-200 ${
                      configured ? "opacity-70 cursor-not-allowed" : ""
                    }`}
                  />
                </div>

                {configured && (
                  <div className="bg-amber-50 dark:!bg-amber-900/20 border border-amber-200 dark:!border-amber-700/40 rounded-xl p-3 text-sm text-amber-800 dark:!text-amber-200 space-y-1">
                    <p>
                      {t("services.printAgent.changeNote") ||
                        t("ui.reconfigureHelp")}
                    </p>
                    <p>
                      {t("services.printAgent.stopFullyNote", {
                        company: company.name,
                      })}
                    </p>
                  </div>
                )}

                <div className="flex justify-end gap-3 pt-1">
                  {configured ? (
                    <button
                      type="button"
                      onClick={() => handleUnconfig(company.key)}
                      disabled={loading}
                      className="px-5 py-2.5 rounded-lg bg-red-500 hover:bg-red-600 text-white text-sm font-medium transition-colors disabled:opacity-50"
                    >
                      {t("services.printAgent.unconfig")}
                    </button>
                  ) : (
                    <button
                      type="button"
                      onClick={() => handleSave(company.key)}
                      disabled={
                        loading ||
                        currentSelected.length === 0 ||
                        !guepexSecret.trim()
                      }
                      className="px-5 py-2.5 rounded-lg bg-[#9b59bf] hover:bg-[#814a9f] text-white text-sm font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {loading ? (
                        <Spin size="small" />
                      ) : (
                        t("services.printAgent.activate")
                      )}
                    </button>
                  )}
                </div>
              </>
            )}

            {/* ecotrack/dhd and ecom */}
            {company.key !== "zrexpress" && company.key !== "guepex" && (
              <>
                <div>
                  <p className="text-sm font-medium text-gray-600 dark:!text-gray-300 mb-4 text-center">
                    {t("services.printAgent.selectEvents")}
                  </p>
                  <div className="flex flex-wrap items-center justify-center gap-3">
                    {events.map((ev) => {
                      const checked = currentSelected[0]?.key === ev.key;
                      return (
                        <button
                          key={ev.key}
                          type="button"
                          disabled={configured}
                          onClick={() =>
                            selectEvent(company.key, ev.key, ev.event)
                          }
                          className={`
                            relative flex items-center gap-2 px-5 py-2.5 rounded-full border-2 text-sm font-medium
                            transition-all duration-200
                            ${configured ? "opacity-60 cursor-not-allowed" : "cursor-pointer hover:scale-[1.02]"}
                            ${
                              checked
                                ? "bg-[#9b59bf] border-[#9b59bf] text-white shadow-md shadow-[#9b59bf]/25"
                                : "bg-[#9b59bf]/10 border-[#9b59bf]/40 text-[#9b59bf] dark:text-[#bc7ade] hover:bg-[#9b59bf]/15"
                            }
                          `}
                        >
                          {checked && (
                            <CheckIcon className="w-4 h-4 text-white shrink-0" />
                          )}
                          <span>
                            {t(`services.printAgent.events.${ev.key}`, {
                              defaultValue: ev.fr,
                            })}
                          </span>
                        </button>
                      );
                    })}
                  </div>
                </div>

                {webhookUrl && (
                  <div>
                    <p className="text-sm font-medium text-gray-600 dark:!text-gray-300 mb-2">
                      {t("services.printAgent.webhookUrl")}
                    </p>
                    <div className="flex items-center gap-2">
                      <input
                        type="text"
                        readOnly
                        value={webhookUrl}
                        className="flex-1 bg-[#fcf6ff] dark:!bg-[#141414] border border-[#fbf4ff] dark:!border-[#3c3a3d] rounded-lg px-3 py-2.5 text-sm font-mono text-gray-800 dark:!text-gray-200 truncate"
                        onClick={(e) => e.target.select()}
                      />
                      <button
                        type="button"
                        onClick={() => copyToClipboard(webhookUrl)}
                        className="flex items-center gap-1.5 px-3 py-2.5 rounded-lg bg-[#9b59bf] hover:bg-[#814a9f] text-white text-sm font-medium shrink-0"
                      >
                        <ClipboardDocumentIcon className="w-4 h-4" />
                        {t("services.printAgent.copy")}
                      </button>
                    </div>
                  </div>
                )}

                <div className="bg-[#fcf6ff] dark:!bg-[#222222] border border-[#fbf4ff] dark:!border-[#3c3a3d] rounded-xl p-4 text-sm text-gray-700 dark:!text-gray-300 leading-relaxed">
                  <p className="font-medium text-[#9e68ba] mb-1">
                    {t("services.printAgent.instructionsTitle")}
                  </p>
                  <ol className="list-decimal list-inside space-y-1">
                    <li>{t("services.printAgent.instruction1")}</li>
                    <li>
                      {company.key === "ecotrack" &&
                        t("services.printAgent.instructionEcotrack")}
                      {company.key === "ecomdelivery" &&
                        t("services.printAgent.instructionEcom")}
                      {company.key === "dhd" &&
                        t("services.printAgent.instructionDhd")}
                    </li>
                    <li>{t("services.printAgent.instruction3")}</li>
                  </ol>
                </div>

                {configured && (
                  <div className="bg-amber-50 dark:!bg-amber-900/20 border border-amber-200 dark:!border-amber-700/40 rounded-xl p-3 text-sm text-amber-800 dark:!text-amber-200 space-y-1">
                    <p>
                      {t("services.printAgent.changeNote") ||
                        t("ui.reconfigureHelp")}
                    </p>
                    <p>
                      {t("services.printAgent.stopFullyNote", {
                        company: company.name,
                      })}
                    </p>
                  </div>
                )}

                <div className="flex justify-end gap-3 pt-1">
                  {configured ? (
                    <button
                      type="button"
                      onClick={() => handleUnconfig(company.key)}
                      disabled={loading}
                      className="px-5 py-2.5 rounded-lg bg-red-500 hover:bg-red-600 text-white text-sm font-medium transition-colors disabled:opacity-50"
                    >
                      {t("services.printAgent.unconfig")}
                    </button>
                  ) : (
                    <button
                      type="button"
                      onClick={() => handleSave(company.key)}
                      disabled={loading || currentSelected.length === 0}
                      className="px-5 py-2.5 rounded-lg bg-[#9b59bf] hover:bg-[#814a9f] text-white text-sm font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {loading ? (
                        <Spin size="small" />
                      ) : (
                        t("services.printAgent.save")
                      )}
                    </button>
                  )}
                </div>
              </>
            )}
          </div>
        )}
      </div>
    );
  };

  return (
    <div
      className="min-h-[480px] dark:!bg-[#181819]"
      dir={isRTL ? "rtl" : "ltr"}
    >
      <div className="px-4 py-6 space-y-6">
        <div className="border border-[#fbf4ff] dark:!border-[#3c3a3d] rounded-xl overflow-hidden">
          <button
            type="button"
            onClick={() => setUnlinkedOpen(!unlinkedOpen)}
            className="w-full flex items-center justify-between p-4 bg-gray-50 dark:!bg-[#1f1f1f] hover:bg-gray-100 dark:hover:bg-[#2a2a2a] transition-colors"
          >
            <span className="font-semibold text-gray-700 dark:!text-gray-200">
              {t("services.printAgent.unlinkedCompanies")} (
              {unlinkedCompanies.length})
            </span>
            {unlinkedOpen ? (
              <ChevronUpIcon className="w-5 h-5 text-gray-400" />
            ) : (
              <ChevronDownIcon className="w-5 h-5 text-gray-400" />
            )}
          </button>

          {unlinkedOpen && (
            <div className="p-4 grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
              {unlinkedCompanies.map((company) => (
                <div
                  key={company.key}
                  className="flex flex-col items-center gap-2 p-3 rounded-xl border border-gray-100 dark:!border-[#2a2a2a] bg-gray-50 dark:!bg-[#181818] opacity-60"
                >
                  <div className="w-10 h-10 rounded-lg bg-white dark:!bg-[#141414] flex items-center justify-center overflow-hidden">
                    {company.logoUrl ? (
                      <img
                        src={company.logoUrl}
                        alt={company.name}
                        className="w-full h-full object-contain"
                      />
                    ) : (
                      <span className="text-xs font-bold text-gray-400">
                        {company.name.slice(0, 2)}
                      </span>
                    )}
                  </div>
                  <span className="text-xs font-medium text-gray-500 text-center">
                    {company.name}
                  </span>
                </div>
              ))}
              {unlinkedCompanies.length === 0 && (
                <p className="col-span-full text-sm text-gray-500 text-center py-4">
                  {t("services.printAgent.allCompaniesLinked")}
                </p>
              )}
            </div>
          )}
        </div>

        <div>
          <h3 className="font-bold text-gray-800 dark:!text-gray-100 mb-3">
            {t("services.printAgent.linkedCompanies")}
          </h3>

          {linkedCompanies.length === 0 ? (
            <div className="bg-amber-50 dark:!bg-amber-900/20 border border-amber-200 dark:!border-amber-700/40 rounded-xl p-5 text-center">
              <p className="text-amber-700 dark:!text-amber-300 text-sm">
                {t("services.printAgent.noLinkedCompany")}
              </p>
            </div>
          ) : (
            <div className="space-y-3">
              {linkedCompanies.map((company) => renderCompanySection(company))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
